/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as fs from "node:fs";
import path from "node:path";
import { Readable } from "node:stream";
import { ExitProcessError } from "@osdk/cli.common";
import { autoVersion, AutoVersionError } from "@osdk/foundry-config-json";
import archiver from "archiver";
import { consola } from "consola";
import { colorize } from "consola/utils";
import prettyBytes from "pretty-bytes";
import { createInternalClientContext, thirdPartyApplications } from "#net";
import { maybeUpdateJemmaCustomMetadata } from "../../../util/maybeUpdateJemmaCustomMetadata.js";
import { loadToken } from "../../../util/token.js";
export default async function siteDeployCommand({
  selectedVersion,
  application,
  foundryUrl,
  uploadOnly,
  snapshot,
  snapshotId,
  directory,
  token,
  tokenFile
}) {
  const loadedToken = await loadToken(token, tokenFile);
  const tokenProvider = () => loadedToken;
  const clientCtx = createInternalClientContext(foundryUrl, tokenProvider);
  let siteVersion;
  if (typeof selectedVersion === "string") {
    siteVersion = selectedVersion;
  } else {
    siteVersion = await findAutoVersion(selectedVersion);
    consola.info(`Auto version inferred next version to be: ${siteVersion}`);
  }
  consola.debug(`Using directory for site files: "${path.resolve(directory)}`);
  const stat = await fs.promises.stat(directory);
  if (!stat.isDirectory()) {
    throw new ExitProcessError(2, "Specified path exists but is not a directory");
  }
  consola.start("Zipping site files");
  const archive = archiver("zip").directory(directory, false);
  logArchiveStats(archive);
  if (snapshot) {
    await uploadSnapshot(clientCtx, application, siteVersion, snapshotId ?? "", archive);
    consola.info("Snapshot mode enabled, skipping deployment");
    return;
  }
  await upload(clientCtx, application, siteVersion, archive);
  let siteLink;
  if (!uploadOnly) {
    const website = await thirdPartyApplications.deployWebsite(clientCtx, application, {
      version: siteVersion
    });
    consola.success(`Deployed ${siteVersion} successfully`);
    const domain = website.subdomains[0];
    if (domain != null) {
      siteLink = `https://${domain}`;
      logSiteLink("View live site:", siteLink);
    }
  } else {
    const website = await thirdPartyApplications.getWebsite(clientCtx, application);
    const domain = website?.subdomains[0];
    consola.info("Upload only mode enabled, skipping deployment");
    if (domain != null) {
      siteLink = `https://${domain}/.system/preview?previewVersion=${siteVersion}`;
      logSiteLink("Preview link:", siteLink);
    }
  }

  // Write the site link and version to the custom metadata file when the site link is present
  if (siteLink != null) {
    maybeUpdateJemmaCustomMetadata({
      siteLink,
      siteVersion
    });
  }
}
async function findAutoVersion(config) {
  try {
    return await autoVersion(config);
  } catch (e) {
    throw new ExitProcessError(2, e instanceof Error ? e.message : undefined, e instanceof AutoVersionError ? e.tip : undefined);
  }
}
async function uploadSnapshot(clientCtx, application, siteVersion, snapshotId, archive) {
  consola.start("Uploading snapshot site files");
  await Promise.all([thirdPartyApplications.uploadSnapshotVersion(clientCtx, application, siteVersion, snapshotId, Readable.toWeb(archive) // This cast is because the dom fetch doesn't align type wise with streams
  ), archive.finalize()]);
  consola.success("Snapshot upload complete");
}
async function upload(clientCtx, application, siteVersion, archive) {
  consola.start("Uploading site files");
  await Promise.all([thirdPartyApplications.uploadVersion(clientCtx, application, siteVersion, Readable.toWeb(archive) // This cast is because the dom fetch doesn't align type wise with streams
  ), archive.finalize()]);
  consola.success("Upload complete");
}
function logArchiveStats(archive) {
  let archiveStats = {
    fileCount: 0,
    bytes: 0
  };
  archive.on("progress", progress => {
    archiveStats = {
      fileCount: progress.entries.total,
      bytes: progress.fs.totalBytes
    };
  });
  archive.on("finish", () => {
    consola.info(`Zipped ${prettyBytes(archiveStats.bytes, {
      binary: true
    })} total over ${archiveStats.fileCount} files`);
  });
}
function logSiteLink(title, link) {
  consola.box({
    message: `${title}\n\n${colorize("green", link)}`,
    style: {
      padding: 2,
      borderColor: "green",
      borderStyle: "rounded"
    }
  });
}